import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import { useSnackbar } from 'notistack';

import modalStyles, { DIALOG_PAPER_SLOT } from './styles';

interface EvolutionModalProps {
  isOpen: boolean;
  handleClose: () => void;
  pacientData: {
    id: number;
    name: string;
  };
}

interface EvolutionModalState {
  text: string;
  created_at: string;
  pictures: File[];
}

const INITIAL_STATE: EvolutionModalState = {
  text: '',
  created_at: '',
  pictures: [],
};

const CLOUD_NAME = import.meta.env.VITE_CLOUD_NAME;
const CLOUDINARY_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;

const EditEvolutionModal = ({
  isOpen = false,
  handleClose,
  pacientData,
}: EvolutionModalProps): JSX.Element => {
  const [formData, setFormData] = useState<EvolutionModalState>(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const imageCols = isXs ? 1 : 2;

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_STATE);
    }
  }, [isOpen]);

  const handleForm = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const { name, value } = event.target;
      setFormData(prevData => ({
        ...prevData,
        [name]: value,
      }));
    },
    [],
  );

  const uploadToCloudinary = useCallback(
    async (file: File): Promise<string> => {
      const formsData = new FormData();
      formsData.append('file', file);
      formsData.append('upload_preset', 'ml_default');

      const response = await fetch(CLOUDINARY_URL, {
        method: 'POST',
        body: formsData,
      });
      const data = (await response.json()) as { secure_url?: string };
      if (!data.secure_url) {
        throw new Error('Upload failed');
      }
      return data.secure_url;
    },
    [],
  );

  const sendData = useCallback(async (): Promise<void> => {
    try {
      setIsSubmitting(true);
      const imageUrls = await Promise.all(
        formData.pictures.map(uploadToCloudinary),
      );

      await axios.post('create-evolution', {
        name: pacientData.name,
        text: formData.text,
        created_at: formData.created_at,
        pacientId: pacientData.id,
        pictures: imageUrls,
      });
      enqueueSnackbar('Enviado com sucesso');
      handleClose();
    } catch {
      enqueueSnackbar('Erro ao enviar os dados', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [enqueueSnackbar, formData, handleClose, pacientData, uploadToCloudinary]);

  const uploadFile = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files?.length) {
      setFormData(prevData => ({
        ...prevData,
        pictures: [...prevData.pictures, ...Array.from(files)],
      }));
    }
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="evolution-add-title"
      slotProps={{ paper: DIALOG_PAPER_SLOT }}
    >
      <DialogTitle id="evolution-add-title" sx={modalStyles.dialogTitle}>
        <Box sx={modalStyles.titleRow}>
          <Typography variant="h6" sx={modalStyles.titleTypography}>
            {`Nova evolução — ${pacientData.name}`}
          </Typography>
          <IconButton edge="end" onClick={handleClose} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={modalStyles.dialogContent}>
        <Stack spacing={3} sx={modalStyles.formStack}>
          <TextField
            type="date"
            name="created_at"
            label="Data"
            value={formData.created_at}
            onChange={handleForm}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            name="text"
            label="Relatório"
            value={formData.text}
            onChange={handleForm}
            multiline
            minRows={6}
            fullWidth
            placeholder="Descreva a evolução do paciente…"
          />
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={modalStyles.photosOverline}
            >
              Fotos
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={isSubmitting}
            >
              Adicionar fotos
              <input
                type="file"
                accept="image/jpeg,image/png"
                hidden
                multiple
                onChange={uploadFile}
              />
            </Button>
          </Box>
          {formData.pictures.length > 0 && (
            <ImageList cols={imageCols} gap={12} sx={modalStyles.imageList}>
              {formData.pictures.map((picture, index) => (
                <ImageListItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={`pic-${index}`}
                  sx={modalStyles.imageListItem}
                >
                  <img
                    src={URL.createObjectURL(picture)}
                    alt={`Pré-visualização ${index + 1}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: 160,
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={modalStyles.dialogActions}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={isSubmitting || !formData.created_at.trim()}
          onClick={() => {
            // eslint-disable-next-line no-void
            void sendData();
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            'Enviar'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEvolutionModal;
